import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Section Satisfaction (NPS) — portée du back-office legacy.
 * Lit les stats via RPC `get_nps_dashboard_stats` et les réponses via la table `nps_responses`
 * (même projet Supabase). Onglets détracteurs/passifs/promoteurs + marquer traité.
 */
type NpsTab = 'detractors' | 'passives' | 'promoters' | 'all';

interface NpsStats {
  ok?: boolean;
  nps_score: number | null;
  total_responses?: number;
  response_rate?: number;
  promoters?: number;
  passives?: number;
  detractors?: number;
  untreated_detractors?: number;
}
interface NpsResponse {
  id: string;
  score: number;
  category: 'detractor' | 'passive' | 'promoter';
  comment: string | null;
  customer_email_snapshot: string | null;
  customer_name_snapshot: string | null;
  treated_at: string | null;
  created_at: string;
}

const CAT_BADGE: Record<string, string> = {
  detractor: 'bg-red-100 text-red-700',
  promoter: 'bg-emerald-100 text-emerald-700',
  passive: 'bg-amber-100 text-amber-700',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export default function NpsSection({ tenantId }: { tenantId: string }) {
  const [stats, setStats] = useState<NpsStats | null>(null);
  const [period, setPeriod] = useState(30);
  const [tab, setTab] = useState<NpsTab>('detractors');
  const [responses, setResponses] = useState<NpsResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: s } = await supabase.rpc('get_nps_dashboard_stats', {
      p_tenant_id: tenantId,
      p_days: period,
    });
    if (s?.ok) setStats(s as NpsStats);
    else setStats(null);
    let q = supabase
      .from('nps_responses')
      .select(
        'id, score, category, comment, customer_email_snapshot, customer_name_snapshot, treated_at, created_at',
      )
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (tab !== 'all') q = q.eq('category', tab.slice(0, -1));
    const { data: r } = await q;
    setResponses((r as unknown as NpsResponse[]) ?? []);
    setLoading(false);
  }, [tenantId, period, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const treat = async (id: string) => {
    const { data } = await supabase.rpc('mark_nps_treated', { p_response_id: id });
    if (data?.ok) load();
  };

  const score = stats?.nps_score ?? null;
  const color = score === null ? '#9CA3AF' : score >= 50 ? '#10B981' : score >= 0 ? '#F59E0B' : '#DC2626';
  const label = score === null ? '—' : score >= 50 ? 'Excellent' : score >= 0 ? 'Correct' : 'À redresser';

  const tabs: { id: NpsTab; label: string }[] = [
    { id: 'detractors', label: '🚨 Détracteurs' },
    { id: 'passives', label: '😐 Passifs' },
    { id: 'promoters', label: '🌟 Promoteurs' },
    { id: 'all', label: 'Toutes' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase opacity-80">💬 Satisfaction clients · NPS</div>
            <div className="mt-1 text-2xl font-black">Vos clients vous recommandent-ils ?</div>
            <div className="mt-2 text-sm opacity-90">
              Email envoyé après chaque commande récupérée. Détecte les insatisfaits et booste vos avis Google.
            </div>
          </div>
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value, 10))}
            className="cursor-pointer rounded-lg border border-white/30 bg-white/20 px-3 py-2 text-sm font-bold text-white backdrop-blur"
          >
            <option value="7" className="text-slate-900">7 jours</option>
            <option value="30" className="text-slate-900">30 jours</option>
            <option value="90" className="text-slate-900">90 jours</option>
          </select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-bold uppercase text-slate-500">Score NPS</div>
          <div className="mt-1 text-4xl font-black" style={{ color }}>
            {score !== null ? score : '—'}
          </div>
          <div className="mt-1 text-xs" style={{ color }}>{label}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-bold uppercase text-slate-500">Réponses {period}j</div>
          <div className="mt-1 text-4xl font-black">{stats?.total_responses ?? 0}</div>
          <div className="mt-1 text-xs text-slate-500">{stats?.response_rate ?? 0}% de réponse</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-bold uppercase text-slate-500">Répartition</div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-sm font-bold text-emerald-600">{stats?.promoters ?? 0} 😊</span>
            <span className="text-sm font-bold text-amber-600">{stats?.passives ?? 0} 😐</span>
            <span className="text-sm font-bold text-red-600">{stats?.detractors ?? 0} 😞</span>
          </div>
        </div>
        <div
          className={
            'rounded-2xl border p-5 ' +
            ((stats?.untreated_detractors ?? 0) > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white')
          }
        >
          <div className="text-xs font-bold uppercase text-slate-500">À traiter</div>
          <div
            className={
              'mt-1 text-4xl font-black ' +
              ((stats?.untreated_detractors ?? 0) > 0 ? 'text-red-700' : 'text-slate-300')
            }
          >
            {stats?.untreated_detractors ?? 0}
          </div>
          <div className="mt-1 text-xs text-slate-500">détracteurs non rappelés</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex border-b border-slate-200">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                'flex-1 px-4 py-3 text-sm font-bold transition ' +
                (tab === t.id ? 'border-b-2 border-slate-900 bg-slate-50 text-slate-900' : 'text-slate-400 hover:text-slate-700')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Chargement…</div>
        ) : responses.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            Aucune réponse dans cette catégorie pour le moment.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {responses.map((r) => (
              <div key={r.id} className="flex items-start gap-4 p-4">
                <div
                  className={
                    'flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl text-2xl font-black ' +
                    (CAT_BADGE[r.category] ?? 'bg-slate-100 text-slate-700')
                  }
                >
                  {r.score}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="font-bold">{r.customer_name_snapshot || 'Anonyme'}</span>
                    <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                    {r.treated_at && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                        ✓ Traité
                      </span>
                    )}
                  </div>
                  <div className="mb-2 text-xs text-slate-500">{r.customer_email_snapshot}</div>
                  {r.comment ? (
                    <div className="rounded-lg bg-slate-50 p-3 text-sm italic text-slate-700">« {r.comment} »</div>
                  ) : (
                    <div className="text-xs italic text-slate-400">Pas de commentaire</div>
                  )}
                </div>
                {r.category === 'detractor' && !r.treated_at && (
                  <button
                    onClick={() => treat(r.id)}
                    className="flex-shrink-0 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    ✓ Marquer traité
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
